
import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../../auth.context';
import * as kypApiService from '../../services/kypApiService';
import { KYPPipelineType } from '../types/kyp-search.types';
import { IndividualArticleSummary } from '../types/kyp-search.types';
import { CategoryPollingManager } from '../services/CategoryPollingManager';

export type KYPPhase = 
  | 'player_search'       // Initial state, searching for players
  | 'static_data_loading' // Player selected, loading profile/stats
  | 'static_data_ready'   // Static data loaded, ready to start KYP search
  | 'kyp_running'         // KYP search in progress
  | 'kyp_complete'        // KYP search completed successfully
  | 'kyp_cancelled';      // KYP search cancelled by user

export interface Player {
  id: string;
  name: string;
  club?: string;
  position?: string;
  age?: number;
}

export interface PlayerProfile {
  id: string;
  name: string;
  full_name: string;
  url: string;
  description: string;
  image_url: string;
  position: string;
  other_positions: string;
  date_of_birth: string;
  place_of_birth_city: string;
  place_of_birth_country: string;
  age: number;
  height: string;
  nationality: string;
  is_retired: boolean;
  foot: string;
  shirt_number: string;
  joined_on: string;
  contract_expires: string;
  signed_from: string;
  market_value: string;
  club_id: string;
  club_name: string;
  agent_name: string;
  updated_at: string;
}

export interface PlayerStats {
  id: string;
  player_id: string;
  competition_id: string;
  competition_name: string;
  season_id: string;
  club_id: string;
  appearances: number;
  goals: number;
  assists: number;
  yellow_cards: number;
  red_cards: number;
  minutes_played: number;
  updated_at: string;
}

export interface PlayerInjury {
  player_id: string;
  season: string;
  injury: string;
  from_date: string;
  until_date: string | null;
  days: number;
  games_missed: number;
  updated_at: string;
}

export interface PlayerTransfer {
  id: string;
  player_id: string;
  from_club_id: string;
  from_club_name: string;
  to_club_id: string;
  to_club_name: string;
  transfer_date: string;
  season: string;
  is_loan: boolean;
  is_loan_end: boolean;
  updated_at: string;
  market_value: string;
  fee: string;
}

export interface PlayerAchievement {
  id: string;
  player_id: string;
  achievement_title: string;
  season_name: string;
  club_name: string;
  club_id: string;
  competition_name: string;
  competition_id: string;
  updated_at: string;
}

export interface SearchProgress {
  progress_percentage: number;
  current_step: string;
  step_progress: number;
  completed_steps: string[];
  step_description?: string;
}

export type SearchStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';

export interface RatingData {
  rating: number;
  explanation: string;
}

export interface PlayerData {
  // Overall player score
  overall_player_score?: number;
  
  // Global article references
  article_references?: Record<string, string>;
  
  // KYP categories
  family?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  financial?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  career?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  personal_life?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  agent?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  personality?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  early_career_and_education?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  adverse_media?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  legal_and_criminal_history?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  sponsorships?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  
  // Legacy fields for backward compatibility
  news?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  performance?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  education?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  compliance?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  legal?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  medical?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  commercial?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  prediction?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  comparables?: { summary?: string; rating?: RatingData; article_references?: Record<string, string>; };
  
  // Debug field
  debug?: {
    all_articles?: any[];
    [key: string]: any;
  };
}

export interface KYPPageState {
  token: string | null;
  // Current phase
  phase: KYPPhase;
  
  // Player selection
  searchQuery: string;
  playerMatches: Player[];
  selectedPlayer: Player | null;
  
  // Static data
  playerProfile: PlayerProfile | null;
  playerStats: PlayerStats[] | null;
  playerCareerStats: Partial<PlayerStats> | null;
  playerInjuries: PlayerInjury[] | null;
  playerTransfers: PlayerTransfer[] | null;
  playerAchievements: PlayerAchievement[] | null;
  
  // KYP search
  searchId: number | null;
  searchStatus: SearchStatus | null;
  searchProgress: SearchProgress | null;
  searchResults: PlayerData | null;
  
  // UI state
  error: string | null;
  isLoading: boolean;
  isSearching: boolean;
  
  // Stacked KYP
  kypPipelineType: KYPPipelineType;
  stackedKYPConfig: {
    minRelevanceThreshold: number;
    forceResummary: boolean;
  };
  individualSummaries: IndividualArticleSummary[];

  // Category polling
  categoryStatuses: Record<string, boolean>;
  completedCategories: any[];
  isPolling: boolean;
}

export interface KYPPageActions {
  // Search and selection
  setSearchQuery: (query: string) => void;
  searchPlayers: (query: string) => Promise<void>;
  selectPlayer: (player: Player) => Promise<void>;
  
  // KYP search actions
  startKYPSearch: () => Promise<void>;
  cancelKYPSearch: () => Promise<void>;
  
  // Clear all state
  clearSearch: () => void;
  
  // Error handling
  dismissError: () => void;
  
  // Stacked KYP actions
  setKYPPipelineType: (type: KYPPipelineType) => void;
  setStackedKYPConfig: (config: Partial<{
    minRelevanceThreshold: number;
    forceResummary: boolean;
  }>) => void;

  // Category polling actions
  setCategoryStatuses: (statuses: Record<string, boolean>) => void;
  addCompletedCategories: (categories: any[]) => void;
  setIsPolling: (isPolling: boolean) => void;
}

export function useKYPPageState(): [KYPPageState, KYPPageActions] {
  // Auth context for API calls
  const { getToken } = useAuth();
  
  // AbortController for API requests
  const abortControllerRef = useRef<AbortController>(new AbortController());
  
  // Polling reference for KYP search status checking
  const pollingRef = useRef<number | null>(null);
  const pollingTimeoutRef = useRef<number | null>(null);
  
  // Category polling manager for parallel categories pipeline
  const categoryPollingManagerRef = useRef<CategoryPollingManager | null>(null);
  
  // Initial state
  const [state, setState] = useState<KYPPageState>({
    token: null,
    // Current phase
    phase: 'player_search',
    
    // Player selection
    searchQuery: '',
    playerMatches: [],
    selectedPlayer: null,
    
    // Static data
    playerProfile: null,
    playerStats: null,
    playerCareerStats: null,
    playerInjuries: null,
    playerTransfers: null,
    playerAchievements: null,
    
    // KYP search
    searchId: null,
    searchStatus: null,
    searchProgress: null,
    searchResults: null,
    
    // UI state
    error: null,
    isLoading: false,
    isSearching: false,
    
    // Stacked KYP
    kypPipelineType: 'parallel_categories',
    stackedKYPConfig: {
      minRelevanceThreshold: 0.3,
      forceResummary: false
    },
    individualSummaries: [],

    // Category polling
    categoryStatuses: {},
    completedCategories: [],
    isPolling: false
  });
  
  // Cleanup function for aborting requests and stopping polling
  const cleanup = useCallback(() => {
    // Abort any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
    }
    
    // Stop polling if active
    if (pollingRef.current) {
      window.clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    
    // Clear polling timeout if active
    if (pollingTimeoutRef.current) {
      window.clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    
    // Stop category polling if active
    if (categoryPollingManagerRef.current) {
      categoryPollingManagerRef.current.stopPolling();
      categoryPollingManagerRef.current = null;
    }
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    const token = getToken();
    if (token) {
      setState(prev => ({ ...prev, token }));
    }
  }, [getToken]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);
  
  // ACTIONS
  
  // Set search query
  const setSearchQuery = useCallback((query: string) => {
    setState(prev => ({
      ...prev,
      searchQuery: query
    }));
  }, []);
  
  // Search for players
  const searchPlayers = useCallback(async (query: string) => {
    if (!query.trim()) {
      setState(prev => ({
        ...prev,
        playerMatches: [],
        isSearching: false
      }));
      return;
    }
    
    try {
      setState(prev => ({
        ...prev,
        isSearching: true,
        error: null
      }));
      
      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Get player matches
      const results = await kypApiService.searchPlayers(query, token, 20);
      
      setState(prev => ({
        ...prev,
        playerMatches: results,
        isSearching: false
      }));
    } catch (error) {
      console.error('Player search error:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to search players',
        isSearching: false,
        playerMatches: []
      }));
    }
  }, [getToken]);
  
  // Calculate career stats from player stats
  const calculateCareerStats = useCallback((stats: PlayerStats[]): Partial<PlayerStats> => {
    if (!stats || stats.length === 0) return {};
    
    const careerStats: Partial<PlayerStats> = {
      appearances: 0,
      goals: 0,
      assists: 0,
      yellow_cards: 0,
      red_cards: 0,
      minutes_played: 0
    };
    
    // Sum up all stats
    stats.forEach(season => {
      careerStats.appearances! += season.appearances || 0;
      careerStats.goals! += season.goals || 0;
      careerStats.assists! += season.assists || 0;
      careerStats.yellow_cards! += season.yellow_cards || 0;
      careerStats.red_cards! += season.red_cards || 0;
      careerStats.minutes_played! += season.minutes_played || 0;
    });
    
    return careerStats;
  }, []);
  
  // Fetch player static data (profile, stats, etc.)
  const fetchPlayerStaticData = useCallback(async (playerId: string) => {
    try {
      setState(prev => ({
        ...prev,
        phase: 'static_data_loading',
        isLoading: true,
        error: null
      }));
      
      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Fetch player profile
      const profile = await kypApiService.fetchPlayerProfile(playerId, token);
      
      if (!profile) {
        throw new Error('Failed to fetch player profile');
      }
      
      // Fetch player stats
      const stats = await kypApiService.fetchPlayerStats(playerId, token);
      
      // Calculate career stats if we have stats
      const careerStats = stats ? calculateCareerStats(stats) : null;
      
      // Fetch player injuries
      const injuries = await kypApiService.fetchPlayerInjuries(playerId, token);
      
      // Fetch player transfers
      const transfers = await kypApiService.fetchPlayerTransfers(playerId, token);
      
      // Fetch player achievements
      const achievements = await kypApiService.fetchPlayerAchievements(playerId, token);
      
      // Update state with all static data
      setState(prev => ({
        ...prev,
        phase: 'static_data_ready',
        playerProfile: profile,
        playerStats: stats,
        playerCareerStats: careerStats,
        playerInjuries: injuries,
        playerTransfers: transfers,
        playerAchievements: achievements,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error fetching player static data:', error);
      setState(prev => ({
        ...prev,
        phase: 'player_search', // Return to search phase on error
        error: error instanceof Error ? error.message : 'Failed to fetch player data',
        isLoading: false
      }));
    }
  }, [getToken, calculateCareerStats]);
  
  // Select a player from search results
  const selectPlayer = useCallback(async (player: Player) => {
    // Clean up any previous requests
    cleanup();
    
    // Update state with selected player
    setState(prev => ({
      ...prev,
      selectedPlayer: player,
      playerMatches: [], // Clear matches after selection
      searchQuery: player.name, // Update search query with player name
      // Clear previous player data
      playerProfile: null,
      playerStats: null,
      playerCareerStats: null,
      playerInjuries: null,
      playerTransfers: null,
      playerAchievements: null,
      searchResults: null,
      searchId: null,
      searchStatus: null,
      searchProgress: null,
      error: null
    }));
    
    // Fetch player static data
    await fetchPlayerStaticData(player.id);
  }, [cleanup, fetchPlayerStaticData]);
  
  // Start KYP search for the selected player
  const startKYPSearch = useCallback(async () => {
    if (!state.selectedPlayer) {
      setState(prev => ({
        ...prev,
        error: 'No player selected'
      }));
      return;
    }
    
    try {
      // Clean up any previous requests
      cleanup();
      
      // Update state to indicate search is starting
      setState(prev => ({
        ...prev,
        phase: 'kyp_running',
        isLoading: true,
        error: null,
        searchStatus: 'pending',
        searchProgress: {
          progress_percentage: 0,
          current_step: 'initialize_database',
          step_progress: 0,
          completed_steps: []
        },
        individualSummaries: [] // Clear any previous summaries
      }));
      
      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      
      // Choose which pipeline to use
      if (state.kypPipelineType === 'parallel_categories' || state.kypPipelineType === 'parallel_categories_test' || state.kypPipelineType === 'parallel_categories_mock') {
        // Parallel categories implementation
        console.log(`Using ${state.kypPipelineType} pipeline`);
        
        const { kypV3ApiService } = await import('../../services/kypV3ApiService');
        
        let searchFunction;
        if (state.kypPipelineType === 'parallel_categories_test') {
          searchFunction = kypV3ApiService.executeParallelCategoryTest;
        } else if (state.kypPipelineType === 'parallel_categories_mock') {
          searchFunction = kypV3ApiService.executeMockParallelCategorySearch;
        } else {
          searchFunction = kypV3ApiService.executeParallelCategorySearch;
        }

        let result;
        if (state.kypPipelineType === 'parallel_categories_mock') {
          result = await searchFunction(
            state.selectedPlayer.id,
            token
          );
        } else {
          result = await searchFunction(
            state.selectedPlayer.id,
            state.selectedPlayer.name,
            token,
            state.stackedKYPConfig.forceResummary
          );
        }
        
        if (result.status === 'started' && result.search_id) {
          // Handle 201 response and initialize polling
          setState(prev => ({
            ...prev,
            searchId: parseInt(result.search_id, 10),
            searchStatus: 'in_progress',
            isLoading: false
          }));
          
          // Initialize category polling
          initializeCategoryPolling(result.search_id);
        } else {
          throw new Error('Failed to start parallel category search');
        }
      } else if (state.kypPipelineType === 'legacy') {
        // Legacy pipeline implementation
        console.log('Using legacy KYP pipeline');
        
        // Start async search
        const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
        const response = await fetch(`${baseUrl}/ai/v1/player/search/async`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            player_id: state.selectedPlayer.id,
            player_name: state.selectedPlayer.name,
            force_refresh: true
          }),
          signal: abortControllerRef.current.signal
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.status === 'error') {
          throw new Error(data.error_message || 'Failed to start search');
        }
        
        // Get search ID from response
        const searchId = data.search_id;
        
        // Update state with search ID
        setState(prev => ({
          ...prev,
          searchId,
          searchStatus: 'in_progress',
          isLoading: false
        }));
        
        // Start polling for search status
        startPolling(searchId);
      } else if (state.kypPipelineType === 'summaries_only') {
        // Summaries only implementation
        console.log('Using summaries only pipeline');
        
        // Import the V3 service and result adapter
        const { kypV3ApiService } = await import('../../services/kypV3ApiService');
        const { adaptStackedKYPResults } = await import('../utils/result-adapters');
        
        // Get config values
        const { minRelevanceThreshold, forceResummary } = state.stackedKYPConfig;
        
        // Start with progress updates
        setState(prev => ({
          ...prev,
          searchStatus: 'in_progress',
          searchProgress: {
            progress_percentage: 0.2,
            current_step: 'Retrieving article summaries',
            step_progress: 0.2,
            completed_steps: []
          }
        }));
        
        // Get summaries only
        const result = await kypV3ApiService.getStackedKYPSummary(
          state.selectedPlayer.id,
          token,
          minRelevanceThreshold,
          forceResummary,
          (status) => {
            // Update progress state based on status
            setState(prev => ({
              ...prev,
              searchStatus: status.status as SearchStatus,
              searchProgress: {
                progress_percentage: status.progress || 0,
                current_step: status.current_step || 'processing',
                step_progress: status.progress || 0,
                completed_steps: []
              }
            }));
          }
        );
        
        // Process results
        if (result.status === 'success') {
          // Import the adapter
          const { adaptStackedKYPResults } = await import('../utils/result-adapters');
          
          // Adapt the stacked KYP results to match the expected format
          const adaptedResults = adaptStackedKYPResults(result);
          
          // For summaries only mode, we create a minimal PlayerData object
          // that focuses on displaying the individual summaries
          const summariesOnlyResults: PlayerData = {
            ...adaptedResults,
            overall_player_score: 0,
            career: {
              summary: "Article summaries only - no final categorization performed",
              rating: {
                rating: 0,
                explanation: "No rating available in summaries only mode"
              }
            },
            debug: {
              pipeline_type: 'summaries_only',
              article_count: result.article_count,
              summary_count: result.summary_count,
              metadata: result.metadata
            }
          };
          
          // Update state with results
          setState(prev => ({
            ...prev,
            phase: 'kyp_complete',
            searchResults: summariesOnlyResults,
            individualSummaries: result.individual_summaries || [],
            searchStatus: 'completed',
            isLoading: false,
            error: null
          }));
        } else {
          throw new Error(result.error_message || 'Failed to retrieve article summaries');
        }
      } else {
        // Stacked KYP pipeline implementation
        console.log('Using stacked KYP pipeline (V3)');
        
        // Import the V3 service and result adapter
        const { kypV3ApiService } = await import('../../services/kypV3ApiService');
        const { adaptStackedKYPResults } = await import('../utils/result-adapters');
        
        // Get config values
        const { minRelevanceThreshold, forceResummary } = state.stackedKYPConfig;
        
        // Start stacked KYP search with progress updates
        const result = await kypV3ApiService.executeStackedKYPSearch(
          state.selectedPlayer.id,
          state.selectedPlayer.name,
          token, // Pass the token for authentication
          minRelevanceThreshold,
          forceResummary,
          (status) => {
            // Update progress state based on status
            setState(prev => ({
              ...prev,
              searchStatus: status.status as SearchStatus,
              searchProgress: {
                progress_percentage: status.progress || 0,
                current_step: status.current_step || 'processing',
                step_progress: status.progress || 0,
                completed_steps: []
              }
            }));
          }
        );
        
        // Process results
        if (result.status === 'success') {
          // Adapt the stacked KYP results to match the expected format
          const adaptedResults = adaptStackedKYPResults(result);
          
          // Update state with results
          setState(prev => ({
            ...prev,
            phase: 'kyp_complete',
            searchResults: adaptedResults,
            individualSummaries: result.individual_summaries || [],
            searchStatus: 'completed',
            isLoading: false,
            error: null
          }));
        } else {
          throw new Error(result.error_message || 'Failed to complete stacked KYP search');
        }
      }
    } catch (error) {
      console.error('Error starting KYP search:', error);
      
      // Don't set error if the request was aborted
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Search request was aborted');
        return;
      }
      
      setState(prev => ({
        ...prev,
        phase: 'static_data_ready', // Return to ready phase on error
        error: error instanceof Error ? error.message : 'Failed to start KYP search',
        isLoading: false
      }));
    }
  }, [state.selectedPlayer, state.kypPipelineType, state.stackedKYPConfig, cleanup, getToken]);
  
  // Start polling for search status
  const startPolling = useCallback((searchId: number) => {
    // Set up interval for polling
    pollingRef.current = window.setInterval(() => {
      checkSearchStatus(searchId);
    }, 3000);
    
    // Set timeout to stop polling after 15 minutes
    pollingTimeoutRef.current = window.setTimeout(() => {
      console.log('Polling timeout reached');
      
      // Stop polling
      if (pollingRef.current) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      
      // Update state with timeout error
      setState(prev => ({
        ...prev,
        error: 'KYP Search timed out due to a system error.',
        searchStatus: 'failed'
      }));
    }, 15 * 60 * 1000); // 15 minutes
    
    // Initial check immediately
    checkSearchStatus(searchId);
  }, []);
  
  // Check search status
  const checkSearchStatus = useCallback(async (searchId: number) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Get search status
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/player/search/async/${searchId}/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: abortControllerRef.current.signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status === 'error') {
        throw new Error(data.error_message || 'Failed to check search status');
      }
      
      let progress: SearchProgress = {
        progress_percentage: 0,
        current_step: 'initialize_database',
        step_progress: 0,
        completed_steps: []
      };
      
      // Extract progress info from response
      if (data.progress) {
        progress = data.progress;
      } else if (data.job && data.job.progress) {
        // Handle legacy format
        const jobProgress = data.job.progress;
        progress = {
          progress_percentage: jobProgress.progress_percentage || 0,
          current_step: jobProgress.current_step || 'initialize_database',
          step_progress: jobProgress.step_progress || 0,
          completed_steps: jobProgress.completed_steps || []
        };
      }
      
      // Get search status
      const searchStatus = data.job?.status || null;
      
      // Update state with progress and status
      setState(prev => ({
        ...prev,
        searchStatus,
        searchProgress: progress
      }));
      
      // Handle search completion
      if (searchStatus === 'completed') {
        // Stop polling
        cleanup();
        
        // Update state to show we're processing results
        setState(prev => ({
          ...prev,
          searchStatus: 'completed',
          searchProgress: {
            ...prev.searchProgress,
            progress_percentage: 100,
            current_step: 'processing_results',
            step_description: 'Processing search results...'
          } as SearchProgress
        }));
        
        // Get search results
        await getSearchResults(searchId);
      }
      
      // Handle search failure or cancellation
      if (searchStatus === 'failed' || searchStatus === 'cancelled') {
        // Stop polling
        cleanup();
        
        setState(prev => ({
          ...prev,
          phase: searchStatus === 'cancelled' ? 'kyp_cancelled' : 'static_data_ready',
          error: searchStatus === 'cancelled' 
            ? 'Search cancelled by user' 
            : `Search failed: ${data.job?.error_message || 'Unknown error'}`
        }));
      }
    } catch (error) {
      console.error('Error checking search status:', error);
      
      // Don't set error if the request was aborted
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Status check request was aborted');
        return;
      }
    }
  }, [getToken, cleanup]);
  
  // Get search results
  const getSearchResults = useCallback(async (searchId: number) => {
    try {
      console.log('Fetching search results for searchId:', searchId);
      
      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Get search results
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/player/search/async/${searchId}/results`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: abortControllerRef.current.signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Search results response:', data);
      
      if (data.status === 'error') {
        throw new Error(data.error_message || 'Failed to get search results');
      }
      
      if (data.status === 'pending') {
        console.log('Search still in progress');
        return;
      }
      
      // Process the results
      // First remove metadata fields
      const { status, message, player_id, ...resultsData } = data;
      
      console.log('Processing results data:', resultsData);
      
      // Validate that we have some data to process
      if (!resultsData || Object.keys(resultsData).length === 0) {
        console.warn('No results data to process');
        setState(prev => ({
          ...prev,
          phase: 'kyp_complete',
          searchResults: {},
          error: null
        }));
        return;
      }
      
      // Import result processor and adapters
      const { processSearchResults } = await import('../utils/result-adapters');
      
      // Process results based on the pipeline type
      let processedResults;
      if (state.kypPipelineType === 'legacy') {
        // Process legacy results
        processedResults = processSearchResults(resultsData);
      } else {
        // For stacked and summaries_only, we already have processed results
        // from the kypV3Service calls, but we'll process them again to ensure consistency
        processedResults = processSearchResults(resultsData);
        
        // Extract individual summaries if available
        if (resultsData.individual_summaries && Array.isArray(resultsData.individual_summaries)) {
          setState(prev => ({
            ...prev,
            individualSummaries: resultsData.individual_summaries
          }));
        }
      }
      
      console.log('Processed search results:', processedResults);
      
      // Update state with results - always transition to kyp_complete
      setState(prev => ({
        ...prev,
        phase: 'kyp_complete',
        searchResults: processedResults,
        searchStatus: 'completed',
        error: null
      }));
    } catch (error) {
      console.error('Error getting search results:', error);
      
      // Don't set error if the request was aborted
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Results request was aborted');
        return;
      }
      
      // Even on error, transition to completed phase but show error message
      setState(prev => ({
        ...prev,
        phase: 'kyp_complete',
        searchResults: null,
        searchStatus: 'failed',
        error: `Failed to load search results: ${error instanceof Error ? error.message : 'Unknown error'}`
      }));
    }
  }, [getToken, state.kypPipelineType]);
  
  // Cancel KYP search
  const cancelKYPSearch = useCallback(async () => {
    if (!state.searchId) {
      console.log('No search to cancel');
      return;
    }
    
    try {
      // Stop polling immediately
      cleanup();
      
      setState(prev => ({
        ...prev,
        phase: 'kyp_cancelled',
        searchStatus: 'cancelled',
        error: 'Search cancelled by user'
      }));
      
      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Cancel search
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/player/search/async/${state.searchId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        console.error(`HTTP error cancelling search: ${response.status}`);
      }
    } catch (error) {
      console.error('Error cancelling search:', error);
      // We've already updated the UI, so no need to set error state here
    }
  }, [state.searchId, cleanup, getToken]);
  
  // Clear search and return to initial state
  const clearSearch = useCallback(() => {
    // Clean up any previous requests
    cleanup();
    
    // Reset state to initial values
    setState(prevState => ({
      ...prevState,
      // Current phase
      phase: 'player_search',
      
      // Player selection
      searchQuery: '',
      playerMatches: [],
      selectedPlayer: null,
      
      // Static data
      playerProfile: null,
      playerStats: null,
      playerCareerStats: null,
      playerInjuries: null,
      playerTransfers: null,
      playerAchievements: null,
      
      // KYP search
      searchId: null,
      searchStatus: null,
      searchProgress: null,
      searchResults: null,
      
      // UI state
      error: null,
      isLoading: false,
      isSearching: false,
      
      // Stacked KYP - preserve the pipeline type and config
      kypPipelineType: state.kypPipelineType,
      stackedKYPConfig: state.stackedKYPConfig,
      individualSummaries: [],

      // Category polling
      categoryStatuses: {},
      completedCategories: [],
      isPolling: false
    }));
  }, [cleanup, state.kypPipelineType, state.stackedKYPConfig]);
  
  // Dismiss error message
  const dismissError = useCallback(() => {
    setState(prev => ({
      ...prev,
      error: null
    }));
  }, []);
  
  // Set KYP pipeline type
  const setKYPPipelineType = useCallback((type: KYPPipelineType) => {
    setState(prev => {
      console.log('Setting kypPipelineType from', prev.kypPipelineType, 'to', type);
      return {
        ...prev,
        kypPipelineType: type
      };
    });
  }, []);
  
  // Set stacked KYP config
  const setStackedKYPConfig = useCallback((config: Partial<{
    minRelevanceThreshold: number;
    forceResummary: boolean;
  }>) => {
    setState(prev => ({
      ...prev,
      stackedKYPConfig: {
        ...prev.stackedKYPConfig,
        ...config
      }
    }));
  }, []);

  const setCategoryStatuses = useCallback((statuses: Record<string, boolean>) => {
    setState(prev => ({ ...prev, categoryStatuses: statuses }));
  }, []);

  const addCompletedCategories = useCallback((categories: any[]) => {
    setState(prev => {
      const newCategories = categories.filter(
        c => !prev.completedCategories.some(pc => pc.category_name === c.category_name)
      );
      return {
        ...prev,
        completedCategories: [...prev.completedCategories, ...newCategories],
      };
    });
  }, []);

  const setIsPolling = useCallback((isPolling: boolean) => {
    setState(prev => ({ ...prev, isPolling }));
  }, []);

  const convertCategoriesToPlayerData = useCallback((categories: any[]): PlayerData => {
    const playerData: PlayerData = {};
    const categoryMapping: Record<string, keyof PlayerData> = {
      'family': 'family',
      'financial': 'financial',
      'career': 'career',
      'personal_life': 'personal_life',
      'agent': 'agent',
      'personality': 'personality',
      'early_career_and_education': 'early_career_and_education',
      'adverse_media': 'adverse_media',
      'legal_and_criminal_history': 'legal_and_criminal_history',
      'sponsorships': 'sponsorships',
      'on-field_comments': 'news',
      'Family_and_relationships': 'family',
      'Financial': 'financial',
      'Career': 'career',
      'Personal_life': 'personal_life',
      'Agent': 'agent',
      'Personality': 'personality',
      'Early_career_and_youth': 'early_career_and_education',
      'Adverse_media': 'adverse_media',
      'Legal_criminal_or_compliance_issues': 'legal_and_criminal_history',
      'Sponsorships_and_commercial': 'sponsorships',
      'On_Field_comments': 'news',
    };

    categories.forEach(category => {
      const fieldName = categoryMapping[category.category_name];
      if (fieldName && category.profile_content) {
        playerData[fieldName] = {
          summary: category.profile_content.summary || 'No summary available',
          rating: category.profile_content.rating,
          article_references: category.profile_content.article_references,
        };
      }
    });

    return playerData;
  }, []);

  // Initialize category polling for parallel categories pipeline
  const initializeCategoryPolling = useCallback((searchId: string) => {
    console.log('[useKYPPageState] Inside initializeCategoryPolling, searchId:', searchId);
    const token = getToken();
    if (!token) {
      setState(prev => ({
        ...prev,
        error: "Authentication token not found."
      }));
      return;
    }

    console.log('[useKYPPageState] Creating CategoryPollingManager...');
    const pollingManager = new CategoryPollingManager(searchId, token, state.kypPipelineType, {
      onStatusUpdate: (statuses) => setCategoryStatuses(statuses),
      onNewCategoryCompleted: (categories) => {
        addCompletedCategories(categories);
      },
      onAllCategoriesCompleted: (allData) => {
        console.log('All categories completed:', allData);
        setState(prev => ({
          ...prev,
          phase: 'kyp_complete',
          searchStatus: 'completed',
          completedCategories: allData,
          searchResults: null, // Prevent legacy card
        }));
      },
      onError: (error) => setState(prev => ({ ...prev, error })),
      onPollingComplete: () => setIsPolling(false),
    });

    categoryPollingManagerRef.current = pollingManager;
    console.log('[useKYPPageState] Starting polling manager...');
    pollingManager.startPolling();
    setIsPolling(true);
  }, [getToken, setCategoryStatuses, setIsPolling, convertCategoriesToPlayerData, state.kypPipelineType]);
  
  // Bundle actions
  const actions: KYPPageActions = {
    setSearchQuery,
    searchPlayers,
    selectPlayer,
    startKYPSearch,
    cancelKYPSearch,
    clearSearch,
    dismissError,
    setKYPPipelineType,
    setStackedKYPConfig,
    setCategoryStatuses,
    addCompletedCategories,
    setIsPolling
  };
  
  return [state, actions];
}
